<template>
	<v-card flat>
		<v-card-title class="search-box mb-4">
			<v-form ref="form">
				<v-row>
					<v-col cols="2">
						<v-text-field
							v-model.number="search.user_id"
							type="number"
							:label="$t('tables.id')"
							color="green"
							clearable
							clear-icon="mdi-close-circle"
						></v-text-field>
					</v-col>
					<v-col cols="3">
						<v-text-field
							v-model="search.first_name"
							:label="$t('first_name')"
							color="green"
							clearable
							clear-icon="mdi-close-circle"
						></v-text-field>
					</v-col>
					<v-col cols="3">
						<v-text-field
							v-model="search.last_name"
							:label="$t('last_name')"
							color="green"
							clearable
							clear-icon="mdi-close-circle"
						></v-text-field>
					</v-col>
					<v-col cols="4">
						<v-select
							auto-select-first
							:items="genders"
							v-model="search.gender"
							item-text="name"
							item-value="id"
							:label="$t('tables.gender')"
							color="green"
							clear-icon="mdi-close-circle"
							clearable
							item-color="green"
						></v-select>
					</v-col>
					<v-col cols="4">
						<v-select
							auto-select-first
							:items="status"
							v-model="search.student_exam_status"
							item-text="name"
							item-value="id"
							:label="$t('tables.status')"
							color="green"
							clear-icon="mdi-close-circle"
							clearable
							item-color="green"
						></v-select>
					</v-col>
					<v-col cols="8" class="d-flex align-items-end">
						<v-subheader>{{ $t("tables.age") }}</v-subheader>
						<v-range-slider
							v-model="search.age"
							min="0"
							max="100"
							light
							color="green"
							:value="[0, 100]"
							track-color="green"
							track-fill-color="green"
							thumb-label
						>
							<template v-slot:prepend>
								<v-text-field
									:value="search.age[0]"
									class="mt-0 pt-0"
									hide-details
									single-line
									type="number"
									style="width: 60px"
									@change="$set(search.age, 0, $event)"
								></v-text-field>
							</template>
							<template v-slot:append>
								<v-text-field
									:value="search.age[1]"
									class="mt-0 pt-0"
									hide-details
									single-line
									type="number"
									style="width: 60px"
									@change="$set(search.age, 1, $event)"
								></v-text-field>
							</template>
						</v-range-slider>
					</v-col>
					<v-col cols="12" class="d-flex align-items-end justify-content-end">
						<v-btn class="ms-4" color="success" @click="searchStudents">
							{{ $t("search") }}
						</v-btn>
						<v-btn color="error" @click="resetSearch">
							{{ $t("reset") }}
						</v-btn>
					</v-col>
				</v-row>
			</v-form>
		</v-card-title>

		<v-card-text>
			<v-data-table
				:headers="tableHeaders"
				:items="students"
				item-key="id"
				show-select
				v-model="selectedStudents"
				:items-per-page="itemsPerPage"
				:server-items-length="totalStudents"
				no-data-text="No Available Data."
				:page="currentPage"
				@update:options="updateStudents"
				checkbox-color="green"
				:footer-props="{
					'items-per-page-options': [10, 20, 30, 40, 50, 100],
				}"
			>
				<template v-slot:top>
					<v-toolbar flat>
						<v-toolbar-title>{{ $t("Students") }}</v-toolbar-title>
						<v-divider class="mx-4" inset vertical></v-divider>
					</v-toolbar>
				</template>
			</v-data-table>
		</v-card-text>

		<v-card-actions>
			<v-spacer></v-spacer>
			<v-btn color="red darken-1" text @click="closeDialogConfirmation">
				{{ $t("Cancel") }}
			</v-btn>
			<v-btn
				color="green darken-1"
				text
				:disabled="!selectedStudents.length"
				@click="sendQuizByMail"
			>
				{{ $t("sendByMail") }}
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import GlobalMixin from "@/GlobalMixin.js";
import moment from "moment";

export default {
	name: "StudentsList",
	mixins: [GlobalMixin],
	props: ["institute_id", "quiz"],
	emits: ["closeDialog", "closeConfirmation"],
	data() {
		return {
			students: [],
			selectedStudents: [],

			//Search inputs
			search: {
				user_id: undefined,
				first_name: "",
				last_name: "",
				gender: "",
				age: [0, 100],
				student_exam_status: undefined,
			},
			genders: [
				{ id: "Male", name: "Male" },
				{ id: "Female", name: "Female" },
			],
			status: [
				{ id: 0, name: "Not Yet" },
				{ id: 1, name: "Taken" },
			],

			//Table Properties
			offset: 0,
			limit: 10,
			currentPage: 1,
			itemsPerPage: 10,
			totalStudents: null,
		};
	},
	methods: {
		/**
		 * Get all students for a specific institute
		 * @param offset offset that shows where to start rows
		 * @param limit limits how many rows is showed in table
		 * @param sortBy to sort a specific column
		 * @param sortOrder to sort ascending or descending
		 * @param search search object to search for a record or set of records
		 * @returns void
		 */
		getAllInstituteStudents(
			offset = 0,
			limit = this.itemsPerPage,
			sortBy,
			sortOrder,
			search
		) {
			const body = {
				quiz_id: this.quiz?.id,
				institute: this.institute_id,
				quiz_assigned_students: true,
				offset,
				limit,
				sort_field: sortBy === "full_name" ? "first_name" : sortBy,
				sort_order:
					typeof sortOrder === "boolean"
						? sortOrder
							? "desc"
							: "asc"
						: undefined,
				...search,
			};

			this.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}quiz/students/`,
					body,
					this.headers
				)
				.then(res => {
					this.totalStudents = res.data.total_records;
					this.students = res.data.students.map(s => {
						const date2 = moment(new Date()).year();
						const date1 = moment(s.date_of_birth).year();
						const age = date2 - date1;
						return {
							...s,
							full_name: `${s.first_name} ${s.last_name}`,
							gender: s.gender ? s.gender : "",
							date_of_birth: s.date_of_birth ? age : "",
						};
					});
				});
		},

		/**
		 * Handle search parameter to maintain sorting with search
		 * @returns searchObj {name, id}
		 */
		handleSearchParameters() {
			return {
				age_lt:
					this.search.age[0] == 0 && this.search.age[1] == 100
						? undefined
						: this.search.age[1],
				age_gt:
					this.search.age[0] == 0 && this.search.age[1] == 100
						? undefined
						: this.search.age[0],
				first_name: this.search.first_name ? this.search.first_name : undefined,
				last_name: this.search.last_name ? this.search.last_name : undefined,
				user_id: this.search.user_id ? this.search.user_id : undefined,
				gender: this.search.gender ? this.search.gender : undefined,
				student_exam_status:
					!this.search.student_exam_status &&
					typeof this.search.student_exam_status !== "number"
						? undefined
						: this.search.student_exam_status,
			};
		},

		/**
		 * Search students after taking values from the form
		 */
		searchStudents() {
			const searchObj = this.handleSearchParameters();
			this.selectedStudents = [];
			this.getAllInstituteStudents(
				this.offset,
				this.itemsPerPage,
				undefined,
				undefined,
				searchObj
			);
		},

		/**
		 * Reset search and get all students
		 */
		resetSearch() {
			this.search = {
				user_id: undefined,
				first_name: "",
				last_name: "",
				gender: "",
				age: [0, 100],
				student_exam_status: undefined,
			};
			this.selectedStudents = [];
			//Get default students
			this.getAllInstituteStudents();
		},

		/**
		 * Close dialge confirmation
		 */
		closeDialogConfirmation() {
			this.$emit("closeConfirmation", this.$t("ConfirmationModal.areYouSure"));
		},

		/**
		 * Update table whenever pagination or sorting change
		 * @param options table information and it's changed based on user clicks
		 */
		updateStudents(options) {
			const sortBy = options.sortBy[0];
			const sortOrder = options.sortDesc[0];

			// Get neccessary values for table (itemsPerPage, currentPage, offset)
			this.offset = options.itemsPerPage * (options.page - 1);
			this.itemsPerPage = options.itemsPerPage;
			this.currentPage = this.offset ? this.offset / this.itemsPerPage + 1 : 1;

			// Get updated students with new params
			this.getAllInstituteStudents(
				this.offset,
				this.itemsPerPage,
				sortBy,
				sortOrder,
				this.handleSearchParameters()
			);
		},

		/**
		 * Send selected students the selected quiz by mail
		 */
		sendQuizByMail() {
			const students = this.selectedStudents.map(s => s.id);
			const body = {
				quiz_id: this.quiz.id,
				institute: this.institute_id,
				students,
			};
			this.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}quiz/send_email_to_students/`,
					body,
					this.headers
				)
				.then(res => {
					Swal.fire({
						position: "center",
						icon: "success",
						title: res.data.message,
						showConfirmButton: false,
						timer: 1500,
					});
					this.$emit("closeDialog");
					this.selectedStudents = [];
				});
		},
	},
	computed: {
		// Table Data
		tableHeaders() {
			return [
				{ text: this.$t("studentList.studentID"), value: "id" },
				{ text: this.$t("studentList.fullName"), value: "full_name" },
				{ text: this.$t("studentList.gender"), value: "gender" },
				{ text: this.$t("studentList.dateOfBirth"), value: "date_of_birth" },
				{ text: this.$t("studentList.status"), value: "quiz_status" },
			];
		},
	},
	watch: {
		/**
		 * Catch every id change from the parent component and search for new records based on it
		 */
		quiz() {
			if (!this.quiz?.id) return;
			this.resetSearch();
		},
	},
};
</script>

<style lang="scss">
.search-box {
	.v-text-field__details,
	.v-messages {
		display: none !important;
	}
}
</style>
